import { DateTime } from "luxon";
import { CalendarHolidays, Identifiable } from "@planzone/dto";

// The timesheet grid is anchored at UTC midnight on every cell. This
// convention is intentional and load-bearing: it keeps the integer-triple
// submissions (saveTimeRecord — {date, month, year}) TZ-agnostic regardless
// of the viewer's browser timezone. See timesheets-date-audit.md §1c & §5.
//
// Every public export deals in DateTime (UTC-zoned) — no JS Date at the
// API boundary. The timesheet consumer tree was migrated alongside this
// module; consumers operate on DateTime end-to-end.

function utcDt(
  y: number,
  m: number,
  d: number,
  h = 0,
  min = 0,
  s = 0,
): DateTime {
  // m is 0-indexed at the API boundary (preserved from the pre-Luxon
  // contract). Luxon is 1-indexed → +1.
  //
  // JS's Date.UTC(y, m, d, ...) rolled out-of-range day values into
  // adjacent months (Date.UTC(y, 4, 32) === June 1; Date.UTC(y, 4, -3) ===
  // April 27). Luxon's DateTime.utc(y, m, d) does NOT — it returns Invalid
  // for out-of-range — so we anchor at the 1st and add (d - 1) days. This
  // matters for isInWeek and padMonthWeeks which both rely on the
  // overflow when crossing month boundaries.
  return DateTime.utc(y, m + 1, 1, h, min, s).plus({ days: d - 1 });
}

function getMonthDates(date: DateTime): DateTime[] {
  const daysNbr = date.daysInMonth ?? 0;
  const dates: DateTime[] = [];
  for (let i = 1; i <= daysNbr; i++) {
    dates.push(utcDt(date.year, date.month - 1, i));
  }
  return dates;
}

function isWeekStart(date: DateTime): boolean {
  // Monday. Luxon weekday: 1=Mon..7=Sun.
  return date.weekday === 1;
}

function startWeek(weeks: DateTime[][], startDate: DateTime): DateTime[][] {
  return [...weeks, [startDate]];
}

function isInWeek([firstDay]: DateTime[], date: DateTime): boolean {
  // Replicates the pre-migration logic: the receiving array's first entry
  // is "the start day of the week" (Monday for full weeks; a padding-day
  // for the partial first/last row of a month grid). The "lastDay" of the
  // week is then `firstDay + daysToAdd` at 23:59:59.
  //
  // daysToAdd formula (preserved, expressed via the historical
  // 0=Sun..6=Sat index):
  //   firstDayIndex = 0 (Sunday)        → daysToAdd = 7 - 6 = 1
  //   firstDayIndex = 1 (Monday)        → daysToAdd = 7 - 0 = 7
  //   firstDayIndex = 2 (Tue)..6 (Sat)  → daysToAdd = 7 - firstDayIndex
  //
  // Convert Luxon's 1=Mon..7=Sun to the historical 0=Sun..6=Sat.
  const firstDayIndex = firstDay.weekday === 7 ? 0 : firstDay.weekday;
  const daysToAdd =
    7 - (firstDayIndex === 0 ? 6 : firstDayIndex === 1 ? 0 : firstDayIndex);
  const lastDay = firstDay
    .plus({ days: daysToAdd })
    .set({ hour: 23, minute: 59, second: 59 });
  return date >= firstDay && date <= lastDay;
}

function findWeekIndex(weeks: DateTime[][], date: DateTime): number {
  return weeks.findIndex((week) => isInWeek(week, date));
}

function addToWeek(weeks: DateTime[][], date: DateTime): DateTime[][] {
  const index = findWeekIndex(weeks, date);
  return index >= 0
    ? [
        ...weeks.slice(0, index),
        [...weeks[index], date],
        ...weeks.slice(index + 1),
      ]
    : [...weeks, [date]];
}

function getWeeks(dates: DateTime[]): DateTime[][] {
  return dates.reduce<DateTime[][]>(
    (weeks, date) =>
      isWeekStart(date) ? startWeek(weeks, date) : addToWeek(weeks, date),
    [],
  );
}

function padMonthWeeks(month: DateTime[][]): DateTime[][] {
  const firstWeek: DateTime[] = new Array(7);
  const lastWeek: DateTime[] = new Array(7);

  const firstWeekFirstDt = month[0][0];
  for (let i = 0; i < 7; i++) {
    firstWeek[i] =
      i < 7 - month[0].length
        ? utcDt(
            firstWeekFirstDt.year,
            firstWeekFirstDt.month - 1,
            i - 7 + month[0].length + 1,
          )
        : month[0][i - (7 - month[0].length)];
  }

  const lastWeekFirstDt = month[month.length - 1][0];
  for (let i = 0; i < 7; i++) {
    lastWeek[i] =
      i >= month[month.length - 1].length
        ? utcDt(
            lastWeekFirstDt.year,
            lastWeekFirstDt.month - 1,
            i + lastWeekFirstDt.day,
          )
        : month[month.length - 1][i];
  }

  return [firstWeek, ...month.slice(1, month.length - 1), lastWeek];
}

function getMonth(date: DateTime): DateTime[][] {
  return padMonthWeeks(getWeeks(getMonthDates(date)));
}

function getCurrentWeek(
  date: DateTime,
  month: DateTime[][],
): { index: number; week: DateTime[] } {
  return month.reduce(
    (foundWeek, week, index) =>
      isInWeek(week, date) ? { index: index + 1, week } : foundWeek,
    { index: 1, week: month[0] },
  );
}

function isWeekEnd(date: DateTime): boolean {
  // Saturday or Sunday. Luxon weekday: 6=Sat, 7=Sun.
  return date.weekday === 6 || date.weekday === 7;
}

function isToday(date: DateTime): boolean {
  // "Today" is the viewer's local calendar day — matches the WBS/Gantt
  // fix. The grid cells stay UTC-anchored (utcDt); only this user-facing
  // "today" check is in local TZ.
  const today = DateTime.local();
  return (
    date.year === today.year &&
    date.month === today.month &&
    date.day === today.day
  );
}

function isOdd(date: DateTime): boolean {
  // Original predicate (preserved): date.getUTCDay() % 2 !== 0 — selects
  // Mon (1), Wed (3), Fri (5). Sun (0) is even. Luxon weekday: 7=Sun, so
  // map 7→0 first to keep the same parity calculation.
  const dayOfWeek = date.weekday === 7 ? 0 : date.weekday;
  return dayOfWeek % 2 !== 0;
}

function initCursor(): DateTime {
  const today = DateTime.utc().startOf("day");
  const { week } = getCurrentWeek(today, getMonth(today));
  return week[week.length - 1];
}

function isHoliday(
  date: DateTime,
  calendarsHolidays: Identifiable<CalendarHolidays>,
): boolean {
  if (!calendarsHolidays.holidays) {
    return false;
  }
  return calendarsHolidays.holidays.some((holiday) => {
    // holiday.holidayDate is a backend-emitted plain-date / ISO string.
    // Parse as UTC so the day component matches the cell's UTC day.
    const h = DateTime.fromISO(holiday.holidayDate, { zone: "utc" });
    return (
      h.isValid &&
      h.day === date.day &&
      h.month === date.month &&
      h.year === date.year
    );
  });
}

function getMonthsTexts(t: Function) {
  return [
    t("calendar.january"),
    t("calendar.february"),
    t("calendar.march"),
    t("calendar.april"),
    t("calendar.may"),
    t("calendar.june"),
    t("calendar.july"),
    t("calendar.august"),
    t("calendar.september"),
    t("calendar.october"),
    t("calendar.november"),
    t("calendar.december"),
  ];
}

function parseAsLocalDate(dateStr: string): DateTime {
  // The function name is historical — it parses a YYYY-MM-DD string into a
  // UTC-anchored DateTime (the timesheet grid's convention).
  const [y, m, d] = dateStr.split("-").map(Number);
  return utcDt(y, m - 1, d);
}

function formatLocalDate(date: DateTime): string {
  // Same historical naming caveat — formats a grid cell to YYYY-MM-DD
  // using its UTC components.
  return date.toFormat("yyyy-MM-dd");
}

function parseTimestamp(isoString: string): DateTime {
  // Collapses an instant (UTC ISO) to its UTC calendar day. Used to
  // project timesheetDeadline / integratedAt onto the grid for the "is
  // this row locked?" predicate. Returns an Invalid DateTime on bad
  // input — callers can check .isValid if needed.
  return DateTime.fromISO(isoString, { zone: "utc" }).startOf("day");
}

function toFixed(nbr: number) {
  return typeof nbr !== "undefined"
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

export {
  utcDt,
  getMonth,
  getCurrentWeek,
  isWeekEnd,
  isToday,
  isOdd,
  initCursor,
  isHoliday,
  getMonthsTexts,
  parseAsLocalDate,
  formatLocalDate,
  parseTimestamp,
  toFixed,
};
